import _ from 'lodash';
import { IJob } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/job';
import { ISource } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/source';
import createCachedSelector from 're-reselect';
import {
  findAssetsBySource,
  getMediaAssetsMap,
} from '../../../../../redux/files/files.selectors';
import { JobsState } from '../../../../../redux/jobs/jobs.reducer';
import { getJobsState } from '../../../../../redux/jobs/jobs.selectors';
import { JobStatus } from '../../../../../redux/jobs/jobs.types';
import { StoreState } from '../../../../../redux/reducers/store.types';
import { MediaAsset } from '../../../../../types/media-assets';
import { MaybeUndef, StringMap } from '../../../../../types/system';
import { stringToStringEnum } from '../../../../../utils/enum';
import { OperationGroupsState } from '../../operation-groups.reducer';
import { getOperationGroupsState } from '../../operation-groups.selectors';
import {
  OperationGroup,
  OperationGroupType,
} from '../../operation-groups.types';
import {
  JobBasedOperation,
  JobBasedOperationGroup,
  JobStartRequestStatus,
} from './job-based-group.types';

const getGroupCacheKey = (state: StoreState, groupId: string): string =>
  groupId;

export const getJobBasedGroup = createCachedSelector(
  getGroupCacheKey,
  getOperationGroupsState,
  (
    groupId: string,
    operationGroupsState: OperationGroupsState,
  ): JobBasedOperationGroup => {
    const opGroup: MaybeUndef<OperationGroup> =
      operationGroupsState.groupsMap[groupId];

    if (!opGroup) {
      throw new Error('Job based operation group was not found');
    }

    if (
      opGroup.type === OperationGroupType.MultipleDownload ||
      opGroup.type === OperationGroupType.Compress
    ) {
      return opGroup;
    }

    throw new Error('Job based operation group was not found');
  },
)(getGroupCacheKey);

export const getJobOfJobBasedGroup = createCachedSelector(
  getJobBasedGroup,
  getJobsState,
  (
    group: JobBasedOperationGroup,
    jobsState: JobsState,
  ): MaybeUndef<IJob<any>> => {
    if (group.jobStartRequestStatus.status !== JobStartRequestStatus.Started) {
      return;
    }

    const { jobId } = group.jobStartRequestStatus;

    if (!jobsState[jobId]) {
      throw new Error(`Job ${jobId} was not found`);
    }

    return jobsState[jobId];
  },
)(getGroupCacheKey);

export const getFilesForDownloadGroup = createCachedSelector(
  getJobBasedGroup,
  getJobOfJobBasedGroup,
  getMediaAssetsMap,
  (
    group: JobBasedOperationGroup,
    job: MaybeUndef<IJob<any>>,
    mediaAssetsMap: StringMap<MediaAsset>,
  ): MaybeUndef<MediaAsset>[] => {
    const sources: ISource[] = job
      ? job.sources
      : _.map(group.fileIdsList, (id: string): ISource => ({ fileId: id }));

    return _.map(sources, _.partial(findAssetsBySource, mediaAssetsMap));
  },
)(getGroupCacheKey);

function getTotalSize(files: MaybeUndef<MediaAsset>[]): MaybeUndef<number> {
  return _.every(files) ? _(files).map('size').sum() : undefined;
}

function toJobStatus(
  jobStartRequestStatus: JobStartRequestStatus,
  job: MaybeUndef<IJob<any>>,
): MaybeUndef<JobStatus> {
  switch (jobStartRequestStatus) {
    case JobStartRequestStatus.Started:
      return job && stringToStringEnum<JobStatus>(JobStatus, job.status);
    case JobStartRequestStatus.Starting:
      return JobStatus.PENDING;
    case JobStartRequestStatus.StartFailed:
    case JobStartRequestStatus.ProcessingFailed:
    default:
      return JobStatus.ERROR;
  }
}

export const getJobBasedOperation = createCachedSelector(
  getJobBasedGroup,
  getJobOfJobBasedGroup,
  getFilesForDownloadGroup,
  (
    group: JobBasedOperationGroup,
    job: MaybeUndef<IJob<any>>,
    files: MaybeUndef<MediaAsset>[],
  ): JobBasedOperation => {
    return {
      job,
      filesNumber: files.length,
      filesSize: getTotalSize(files),
      status: toJobStatus(group.jobStartRequestStatus.status, job),
    };
  },
)(getGroupCacheKey);
